import { createInertiaApp, router } from '@inertiajs/react'; // Add router import
import { createElement, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { PageProps } from '@/types';
import Layout from '../components/Layout';
import { UserProvider } from '../src/context/UserContext';
import { MediaQueryProvider } from '../src/context/MediaQueryContext';
import { setupMetaUpdater } from '../components/MetaTagUpdater';
import { wcwdTheme } from '../theme';
import { recordPageView } from '../src/utils/recentlyViewed';
import { addCsrfHeaderToVisit } from '../src/utils/csrf';
import { PageChangeLoader } from '../components/ui/Loaders/PageChangeLoader';
import ThemedNotifications from '../components/ui/Notifications/ThemedNotifications';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/notifications/styles.css';

type PageComponent<P = {}> = React.FC<P> & {
  layout?: (page: ReactNode) => ReactNode;
  useBlankLayout?: boolean;
};

// Configure Inertia router defaults - this will ensure preserveUrl is false by default
router.on('before', (event) => {
  // Set preserveUrl to false for all navigation events
  event.detail.visit.preserveUrl = false;
});

// Every non-GET visit carries the meta-tag CSRF token (#1997).
router.on('before', (event) => addCsrfHeaderToVisit(event.detail.visit));

// Global 403 handler — surfaces rack-attack blocks (and other server-side
// permission denials) as a Mantine toast instead of leaving the user on a
// dead JSON payload from an Inertia XHR that couldn't be rendered.
router.on('invalid', (event) => {
  const response = event.detail.response;
  if (!response || response.status !== 403) return;

  let message = 'Access temporarily blocked. Contact support@wherecanwedance.com if this looks wrong.';
  try {
    const body = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
    if (body?.error) message = body.error;
  } catch {
    // Non-JSON body — keep the default message.
  }

  event.preventDefault();
  showNotification({
    color: 'red',
    title: 'Blocked',
    message,
    autoClose: 8000,
  });
});

// Remember show pages for the spotlight's "Recently viewed" (#1504). Fires on
// every page visit, including the first load and back/forward.
router.on('navigate', (event) => recordPageView(event.detail.page));

// Initialize the meta tag updater
setupMetaUpdater();

// The success listener in the MetaTagUpdater will replace this one,
// but we'll keep it for now for debugging purposes
router.on('success', () => {
  // Force update browser history state to fix browser share functionality
  history.replaceState({ ...history.state }, document.title, window.location.href);
});

// Lazy page map: each value is `() => import('../pages/<name>.tsx')`, so a page's
// chunk is only fetched when that page actually renders — instead of `eager: true`,
// which bundled every page into the initial payload (~100 chunks, one 8.5 MB, on
// every load). Vite caches each module after first import, so mutating `Page.layout`
// still persists across visits. A page import that fails after a deploy (a stale
// hash) is caught by the reload guard in application.html.erb, which listens for
// the rejected import as well as failed <script>/<link> loads (#1468, #2126). See #1469.
const pages = import.meta.glob<{
  default: PageComponent<PageProps>;
}>('../pages/**/*.tsx');

// The server <title> (meta-tags: set_meta_tags or the layout default) carries
// the `inertia` attribute, so Inertia's head manager owns it. Pages with
// `<Head title>` replace it; pages without one fall back to what the server
// rendered instead of ending up with no title at all (#1652). That server
// title only describes the URL it was rendered for, so client-side visits to
// other Head-less pages get the site default (mirrors
// ApplicationHelper#default_meta_tags). The match is on the path: a
// query-only visit (?tab=, the schedule's ?view=full) is still the same page,
// and comparing the full URL reset the event page's title (#1808).
const DEFAULT_TITLE = 'All your dance events in one freaking place! | WhereCanWeDance.com';
const serverTitle = document.title;
const serverTitlePath = window.location.pathname;
const fallbackTitle = () =>
  window.location.pathname === serverTitlePath ? serverTitle : DEFAULT_TITLE;

const app = document.getElementById('app');
if (app) {
  createInertiaApp({
    title: (title) => title || fallbackTitle(),
    // PageChangeLoader replaces the default top bar with a dance count (#1885).
    progress: false,
    resolve: (name) => {
      const importPage = pages[`../pages/${name}.tsx`];
      if (!importPage) {
        // console.error(`❌ Missing Inertia page component: '${name}.tsx'`);
        throw new Error(`Missing Inertia page: ${name}`);
      }
      return importPage().then((module) => {
        // A chunk preload failed and the reload guard in application.html.erb
        // preventDefault()ed `vite:preloadError`, which makes Vite resolve the
        // import with undefined instead of rejecting. A reload is already on
        // its way, so wait for it rather than throwing on `module.default` (#2579).
        if (!module) return new Promise<never>(() => {});
        const Page = module.default as PageComponent<PageProps>;
        if (!Page.layout) {
          Page.layout = (page) => createElement(Layout, { children: page });
        }
        return Page;
      });
    },
    setup({ el, App, props }) {
      createRoot(el).render(
        createElement(MantineProvider, { theme: wcwdTheme },
          createElement(ThemedNotifications),
          createElement(PageChangeLoader),
          createElement(MediaQueryProvider, {},
          createElement(UserProvider, {},
            createElement(App, props)
            )
          )
        )
      );
    },
  });
}
